import { createPortal } from 'react-dom';
import { useEffect, useState } from 'react';
import { useAuthStore, useSessionKickedStore } from '@/store/auth';
import { useLocale, useT } from '@/i18n';

const LOCALE_TAG: Record<string, string> = {
  en: 'en-US', zh: 'zh-CN', 'zh-tw': 'zh-TW', ja: 'ja-JP', ko: 'ko-KR', vi: 'vi-VN',
};

function formatCountry(code: string, tag: string): string {
  if (!code) return '—';
  try {
    return new Intl.DisplayNames([tag], { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
}

function formatLoginTime(iso: string, tag: string): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(tag, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function SessionKickedPopup() {
  const t = useT('common');
  const locale = useLocale();
  const tag = LOCALE_TAG[locale] ?? 'en-US';
  const payload = useSessionKickedStore((s) => s.payload);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!payload) { setVisible(false); return; }
    const id = setTimeout(() => setVisible(true), 16);
    return () => clearTimeout(id);
  }, [payload]);

  function handleDismiss() {
    setVisible(false);
    setTimeout(() => {
      useSessionKickedStore.getState().dismiss();
      useAuthStore.getState().clear();
    }, 300);
  }

  if (!payload) return null;

  return createPortal(
    <>
      <div
        aria-hidden
        className="fixed inset-0 transition-opacity duration-300"
        style={{ zIndex: 9998, opacity: visible ? 1 : 0, backgroundColor: 'rgba(0,0,0,0.5)' }}
      />

      <div
        role="alertdialog"
        aria-modal
        aria-labelledby="session-kicked-title"
        className="fixed inset-x-0 top-1/2 mx-auto w-[calc(100%-2.5rem)] max-w-[22rem] overflow-hidden rounded-[1.25rem] bg-white shadow-2xl transition-all duration-300"
        style={{
          zIndex: 9999,
          opacity: visible ? 1 : 0,
          transform: `translateY(calc(-50% + ${visible ? '0px' : '12px'}))`,
        }}
      >
        <div className="px-6 pb-6 pt-3">
          <h2
            id="session-kicked-title"
            className="text-center text-[1rem] font-bold leading-snug text-black"
          >
            {t('session_kicked_title')}
          </h2>

          <p className="mt-2 text-center text-[0.8125rem] leading-[1.5] text-black/60">
            {t('session_kicked_body')}
          </p>

          <p className="mt-4 text-[0.75rem] font-semibold text-black/40">
            {t('session_kicked_location_label')}
          </p>
          <div className="mt-1.5 divide-y divide-gray-100 rounded-xl bg-gray-50 px-4 py-1">
            <Row label={t('session_kicked_country')} value={formatCountry(payload.country, tag)} />
            <Row label={t('session_kicked_ip')} value={payload.ip || '—'} />
            <Row label={t('session_kicked_login_time')} value={formatLoginTime(payload.login_time, tag)} />
          </div>

          <button
            type="button"
            onClick={handleDismiss}
            className="mt-5 flex h-11 w-full items-center justify-center rounded-full bg-brand text-[0.875rem] font-semibold text-white"
          >
            {t('session_kicked_ok')}
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2.5">
      <span className="shrink-0 text-[0.75rem] text-black/40">{label}</span>
      <span className="break-all text-right text-[0.8125rem] font-medium text-black">{value}</span>
    </div>
  );
}
